import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import { interpolate } from 'utils/string';

import { PROJECT_DETAILS_URL } from 'constants/endpoints';
import { ALLOCATION_SEGMENT_COLORS, MY_TEAM_LABELS } from 'constants/myTeam';

import type { TeamAllocationItem } from '../types';

interface TeamsAllocationInfoProps {
  currentAllocations: TeamAllocationItem[];
  hoveredIndex?: number | null;
}

const TeamsAllocationInfo = ({ currentAllocations, hoveredIndex = null }: TeamsAllocationInfoProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (hoveredIndex !== null && itemRefs.current[hoveredIndex]) {
      itemRefs.current[hoveredIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hoveredIndex]);

  return (
    <div ref={popoverRef} className="team-allocations-info my-team__allocation-popover">
      <p className="team-allocations-info__title">{MY_TEAM_LABELS.ALLOCATIONS}</p>
      <div>
        {currentAllocations.map((teamAllocation, index) => {
          const segmentColor = ALLOCATION_SEGMENT_COLORS[index % ALLOCATION_SEGMENT_COLORS.length];
          const isHovered = hoveredIndex === index;

          return (
            <div
              key={teamAllocation?.relation ?? `${teamAllocation?.name}-${index}`}
              ref={(el) => void (itemRefs.current[index] = el)}
              className={classNames('team-allocations-info__items', {
                'team-allocations-info__items--hovered': isHovered,
              })}
            >
              <div className="my-team__allocation-main">
                <div className="my-team__allocation-name-row">
                  <div
                    className="my-team__allocation-swatch"
                    style={{
                      background: segmentColor,
                      marginTop: '6px',
                    }}
                  />
                  <div className="team-allocations-info__items--body">
                    {teamAllocation.type === 'project' ? (
                      <a
                        href={interpolate(PROJECT_DETAILS_URL, { id: teamAllocation?.projectId })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="team-allocations-info__items--name team-allocations-info__items--link">
                          {teamAllocation?.name}
                        </span>
                      </a>
                    ) : (
                      <p className="team-allocations-info__items--name">{teamAllocation?.name}</p>
                    )}
                  </div>
                </div>
                <div className="team-allocations-info__items--hour my-team__allocation-hour">
                  <div className="my-team__allocation-hour-bar" />
                  <span className="team-allocations-info__items--hour-count">
                    {teamAllocation?.allocation}
                    {MY_TEAM_LABELS.HOUR_SUFFIX}
                  </span>
                  <span className="team-allocations-info__items--dot">&middot;</span>
                  <span className="team-allocations-info__items--billing-status">{teamAllocation.billingStatus}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamsAllocationInfo;
